import type { Centavos } from '@abastece/shared';
import { pideReconteo } from '../lib/dia.js';
import type { Clave, Valores } from '../i18n/index.js';
import type { Paso } from './Asistente.js';
import { CONTEO_VACIO, ContarBilletes, conteoEmpezado, totalDelConteo, type Conteo, type Moneda } from './ContarBilletes.js';
import { Ico } from './Iconos.js';

/** A count of one currency, and what came of its first try. */
export interface Contando {
  conteo: Conteo;
  /**
   * What the count came to the first time the step went on, when there was something to hold it
   * against. It is held against it that once and never again: going back and counting differently
   * would otherwise ask, as often as anybody liked, whether a figure is near what the drawer
   * should hold, which is the one thing a blind count must not say.
   */
  primero: Centavos | null;
  /** That first count was off: the form was emptied for a second one. */
  otraVez: boolean;
}

export const SIN_CONTAR: Contando = { conteo: CONTEO_VACIO, primero: null, otraVez: false };

/** What the count came to: what was typed, nothing typed being none. */
export const contado = (c: Contando, moneda: Moneda): Centavos | null => totalDelConteo(c.conteo, moneda);

/**
 * The first count, for the till's record and the owner's eyes: when the drawer was counted again,
 * and when the count that stands is not the one first given, gone back to and changed.
 */
export const primerConteo = (c: Contando, final: Centavos | null): Centavos | null =>
  c.primero !== null && final !== null && (c.otraVez || c.primero !== final) ? c.primero : null;

/**
 * A wizard's step that counts one currency of the drawer, note by note, held against `esperado`:
 * off by `umbral` córdobas or more, or by any dollar, it is asked once more before it is taken,
 * without a word of by how much or which way (`pideReconteo`). The second count stands, and the
 * first goes with it for the owner to see. Nothing expected (a float brought from general cash, the
 * dollars at opening), nothing is held against it.
 *
 * Dollars have «No hay dólares»: an empty drawer is said in one tap, and held against what was
 * expected like any other count. Córdobas have «No hay córdobas» the same way where the step asks
 * for it (`sinCordobas`): a till's opening and its corte (the owner, 2026-10-14).
 */
export function pasoDeConteo({
  id,
  moneda,
  contando,
  poner,
  esperado,
  umbral,
  titulo,
  explicacion,
  guia,
  sinCordobas = false,
  nota,
  siguiente,
  t,
}: {
  id: string;
  moneda: Moneda;
  contando: Contando;
  poner: (c: Contando) => void;
  esperado: Centavos | null;
  umbral: number;
  titulo: string;
  explicacion: string;
  /** A lesson's anchor on the step, where a lesson points at it. */
  guia?: string;
  /** «No hay córdobas» at the top, as dollars always have «No hay dólares». */
  sinCordobas?: boolean;
  /** Said under the explanation, and again if the count is asked for again. */
  nota?: string;
  siguiente: () => void;
  t: (clave: Clave, valores?: Valores) => string;
}): Paso {
  const total = contado(contando, moneda);
  const reconteo = contando.otraVez;

  /** On, with `conteo` as the count: held against what was expected the first time only. */
  function seguir(conteo: Conteo, cuanto: Centavos) {
    if (contando.primero === null && esperado !== null) {
      if (pideReconteo(cuanto, esperado, moneda, umbral)) {
        poner({ conteo: CONTEO_VACIO, primero: cuanto, otraVez: true });
        return;
      }
      poner({ conteo, primero: cuanto, otraVez: false });
    } else {
      poner({ ...contando, conteo });
    }
    siguiente();
  }

  return {
    id,
    titulo: reconteo ? t('turno.otraVez') : titulo,
    ...(guia ? { guia } : {}),
    explicacion: (
      <>
        <p>{reconteo ? t('turno.otraVezExplicacion') : explicacion}</p>
        {nota && (
          <p className="mt-2 font-medium text-slate-100" data-conteo-nota>
            {nota}
          </p>
        )}
      </>
    ),
    contenido: (
      <div className="flex flex-col gap-4">
        {(moneda === 'USD' || sinCordobas) && (
          <button type="button" className="btn-secundario" onClick={() => seguir(CONTEO_VACIO, 0 as Centavos)} data-conteo-vacio={moneda}>
            <Ico icono="listo" />
            {t(moneda === 'USD' ? 'turno.sinDolares' : 'turno.sinCordobas')}
          </button>
        )}
        <ContarBilletes
          // A new count starts empty: the first one's notes are not left in the boxes to be agreed with.
          key={reconteo ? 'segundo' : 'primero'}
          moneda={moneda}
          conteo={contando.conteo}
          onCambio={(conteo) => poner({ ...contando, conteo })}
        />
      </div>
    ),
    seguir: {
      etiqueta: t('asistente.siguiente'),
      deshabilitado: total === null || !conteoEmpezado(contando.conteo),
      alTocar: () => total !== null && seguir(contando.conteo, total),
    },
  };
}
